import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import org.nfeliciano.components.layouts.MarkdownLayout as _org_nfeliciano_components_layouts_MarkdownLayout
import org.nfeliciano.components.layouts.PageLayout as _org_nfeliciano_components_layouts_PageLayout
import org.nfeliciano.pages.AboutPage as _org_nfeliciano_pages_AboutPage
import org.nfeliciano.pages.Index as _org_nfeliciano_pages_Index

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Nuno Feliciano"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "org.nfeliciano.components.layouts.MarkdownLayout",
            parentLayoutId = "org.nfeliciano.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _org_nfeliciano_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "org.nfeliciano.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _org_nfeliciano_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            initRouteMethod = { ctx ->
                org.nfeliciano.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _org_nfeliciano_pages_Index()
        }
        ctx.router.register(
            "/about",
            layoutId = "org.nfeliciano.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                org.nfeliciano.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _org_nfeliciano_pages_AboutPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("about-image", com.stevdza.san.styles.AboutImageStyle)
        ctx.theme.registerStyle("about-text", com.stevdza.san.styles.AboutTextStyle)
        ctx.theme.registerStyle("back-to-top-button", com.stevdza.san.styles.BackToTopButtonStyle)
        ctx.theme.registerStyle("input", com.stevdza.san.styles.InputStyle)
        ctx.theme.registerStyle("navigation-item", com.stevdza.san.styles.NavigationItemStyle)
        ctx.theme.registerStyle("logo", com.stevdza.san.styles.LogoStyle)
        ctx.theme.registerStyle("social-link", com.stevdza.san.styles.SocialLinkStyle)
        ctx.theme.registerStyle("main-button", com.stevdza.san.styles.MainButtonStyle)
        ctx.theme.registerStyle("main-image", com.stevdza.san.styles.MainImageStyle)
        ctx.theme.registerStyle("new-like-section", com.stevdza.san.styles.NewLikeSectionStyle)
        ctx.theme.registerStyle("likes-arrow-icon", com.stevdza.san.styles.LikesArrowIconStyle)
        ctx.theme.registerStyle("service-card", com.stevdza.san.styles.ServiceCardStyle)
        ctx.theme.registerStyle("headline-text", org.nfeliciano.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", org.nfeliciano.SubheadlineTextStyle)
        ctx.theme.registerStyle("markdown", org.nfeliciano.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", org.nfeliciano.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("footer", org.nfeliciano.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", org.nfeliciano.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("hero-container", org.nfeliciano.pages.HeroContainerStyle)
        ctx.theme.registerStyle("home-grid", org.nfeliciano.pages.HomeGridStyle)
        ctx.theme.registerStyle("home-grid-cell", org.nfeliciano.pages.HomeGridCellStyle)
        ctx.theme.registerVariant("-circle", org.nfeliciano.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", org.nfeliciano.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", org.nfeliciano.components.sections.SideMenuSlideInAnim)
        com.stevdza.san.updateTheme(ctx)
        org.nfeliciano.initColorMode(ctx)
        org.nfeliciano.initStyles(ctx)
        org.nfeliciano.initSiteStyles(ctx)
        org.nfeliciano.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        org.nfeliciano.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
